import React from "react";
import { useTranslation } from "react-i18next";

import { Box, Divider, Grid2 as Grid, InputAdornment, TextField, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Select } from "components/forms";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { useDateFormatSelect } from "store";
import toIsoMidday from "utils/to-iso-midday";

import { FloatTypeSelect } from "./form-fields/FloatTypeSelect";
import { RateFixingTypeSelect } from "./form-fields/RateFixingTypeSelect";
import { ReferenceIndexSelect } from "./form-fields/ReferenceIndexSelect";
import { FormValues } from "./schemas";

interface Props {
  formik: FormikProps<FormValues>;
  updateIndexValue: (index: string, date: string | null, type: "principal" | "interest" | "pik") => Promise<void>;
  size?: "small" | "large";
  disabledFields?: string[];
  hiddenFields?: string[];
}

export const AmortizationDrawerPIKSection: React.FC<Props> = ({
  formik,
  updateIndexValue,
  size = "small",
  disabledFields = [],
  hiddenFields = []
}) => {
  const { t } = useTranslation("debt");
  const isDisabled = !formik.values.pik_base_rate && !formik.values.pik_reference_index;
  const dateFormat = useDateFormatSelect();
  const gridSize = size === "large" ? 3 : 4;
  return (
    <Box>
      <Box sx={{ mb: 5 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {t("pikSection.title")}
        </Typography>
        <Divider />
      </Box>
      <Grid container spacing={5}>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("pik_base_rate") ? "none" : "block" }}>
          <TextField
            fullWidth
            disabled={disabledFields.includes("pik_base_rate")}
            type="number"
            name="pik_base_rate"
            label={t("pikSection.baseRate")}
            value={formik.values.pik_base_rate}
            onChange={formik.handleChange}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }
            }}
            error={formik.touched.pik_base_rate && Boolean(formik.errors.pik_base_rate)}
            helperText={formik.touched.pik_base_rate && formik.errors.pik_base_rate}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("pik_accumulated") ? "none" : "block" }}>
          <FloatTypeSelect
            name="pik_float_type"
            label={t("pikSection.interestType")}
            formik={formik}
            disabled={disabledFields.includes("pik_float_type")}
            size={size === "large" ? "medium" : "small"}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("pik_rate_fixing") ? "none" : "block" }}>
          <RateFixingTypeSelect
            name="pik_rate_fixing"
            label={t("pikSection.rateFixing")}
            formik={formik}
            disabled={disabledFields.includes("pik_rate_fixing") || formik.values.pik_float_type !== "spread"}
            size={size === "large" ? "medium" : "small"}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("pik_reference_index") ? "none" : "block" }}>
          <ReferenceIndexSelect
            name="pik_reference_index"
            formik={formik}
            label={t("pikSection.linkage")}
            disabled={formik.values.pik_float_type === "fixed"}
            size={size === "large" ? "medium" : "small"}
            onIndexChange={(index) => updateIndexValue(index, formik.values.pik_reference_day || null, "pik")}
            onIndexClear={() => {
              formik.setFieldValue("pik_link_percent", 0);
              formik.setFieldValue("pik_index_value", null);
            }}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("pik_index_value") ? "none" : "block" }}>
          <TextField
            fullWidth
            disabled
            name="pik_index_value"
            label={t("pikSection.index")}
            value={formik.values.pik_index_value || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("pik_link_percent") ? "none" : "block" }}>
          <TextField
            fullWidth
            type="number"
            name="pik_link_percent"
            label={t("pikSection.percentage")}
            value={formik.values.pik_link_percent}
            onChange={formik.handleChange}
            error={formik.touched.pik_link_percent && Boolean(formik.errors.pik_link_percent)}
            helperText={formik.touched.pik_link_percent && formik.errors.pik_link_percent}
            disabled={isDisabled || formik.values.pik_float_type === "fixed"}
          />
        </Grid>
        <Grid
          size={gridSize}
          sx={{ display: hiddenFields.includes("pik_first_capitalization_date") ? "none" : "block" }}
        >
          <DesktopDatePicker
            label={t("pikSection.capitalizationDate")}
            format={dateFormat}
            views={["year", "month", "day"]}
            value={
              formik.values.pik_first_capitalization_date ? dayjs(formik.values.pik_first_capitalization_date) : null
            }
            onChange={(newValue) =>
              formik.setFieldValue("pik_first_capitalization_date", newValue ? toIsoMidday(newValue) : "")
            }
            sx={{ width: "100%" }}
            disabled={disabledFields.includes("pik_first_capitalization_date") || isDisabled}
            slotProps={{
              textField: {
                error:
                  formik.touched.pik_first_capitalization_date && Boolean(formik.errors.pik_first_capitalization_date),
                helperText: formik.touched.pik_first_capitalization_date && formik.errors.pik_first_capitalization_date
              }
            }}
          />
        </Grid>
        <Grid
          size={gridSize}
          sx={{ display: hiddenFields.includes("pik_capitalization_frequency") ? "none" : "block" }}
        >
          <Select
            name="pik_capitalization_frequency"
            label={t("pikSection.capitalizationFrequency")}
            value={formik.values.pik_capitalization_frequency}
            variant="outlined"
            fullWidth
            disabled={isDisabled || disabledFields.includes("pik_capitalization_frequency")}
            items={[
              { key: "month", value: "month", text: t("common.monthly") },
              { key: "quarter", value: "quarter", text: t("common.quarterly") },
              { key: "semi-annual", value: "semi-annual", text: t("common.semiAnnual") },
              { key: "year", value: "year", text: t("common.annual") }
            ]}
            error={formik.touched.pik_capitalization_frequency && Boolean(formik.errors.pik_capitalization_frequency)}
            helperText={
              formik.touched.pik_capitalization_frequency ? formik.errors.pik_capitalization_frequency : undefined
            }
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("pik_reference_day") ? "none" : "block" }}>
          <DesktopDatePicker
            label={t("pikSection.referenceDate")}
            format={dateFormat}
            views={["year", "month", "day"]}
            value={formik.values.pik_reference_day ? dayjs(formik.values.pik_reference_day) : null}
            onChange={(newValue) => {
              formik.setFieldValue("pik_reference_day", newValue ? toIsoMidday(newValue) : "");
              if (formik.values.pik_reference_index) {
                updateIndexValue(formik.values.pik_reference_index, newValue ? toIsoMidday(newValue) : null, "pik");
              }
            }}
            sx={{ width: "100%" }}
            disabled={isDisabled || formik.values.pik_float_type === "fixed"}
            slotProps={{
              textField: {
                error: formik.touched.pik_reference_day && Boolean(formik.errors.pik_reference_day),
                helperText: formik.touched.pik_reference_day && formik.errors.pik_reference_day
              }
            }}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("pik_reference_day_offset") ? "none" : "block" }}>
          <TextField
            fullWidth
            disabled={isDisabled || formik.values.pik_float_type === "fixed"}
            type="number"
            name="pik_reference_day_offset"
            label="Reference Day Offset"
            value={formik.values.pik_reference_day_offset ?? ""}
            onChange={formik.handleChange}
            error={formik.touched.pik_reference_day_offset && Boolean(formik.errors.pik_reference_day_offset)}
            helperText={formik.touched.pik_reference_day_offset && formik.errors.pik_reference_day_offset}
            slotProps={{ input: { inputProps: { step: 1 } } }}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("pik_floor_rate") ? "none" : "block" }}>
          <TextField
            fullWidth
            type="number"
            name="pik_floor_rate"
            label={t("pikSection.floorRate")}
            value={formik.values.pik_floor_rate}
            onChange={formik.handleChange}
            error={formik.touched.pik_floor_rate && Boolean(formik.errors.pik_floor_rate)}
            helperText={formik.touched.pik_floor_rate && formik.errors.pik_floor_rate}
            disabled={isDisabled || disabledFields.includes("pik_floor_rate")}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }
            }}
          />
        </Grid>
        <Grid size={gridSize} sx={{ display: hiddenFields.includes("pik_ceiling_rate") ? "none" : "block" }}>
          <TextField
            fullWidth
            type="number"
            name="pik_ceiling_rate"
            label={t("pikSection.ceilingRate")}
            value={formik.values.pik_ceiling_rate}
            onChange={formik.handleChange}
            error={formik.touched.pik_ceiling_rate && Boolean(formik.errors.pik_ceiling_rate)}
            helperText={formik.touched.pik_ceiling_rate && formik.errors.pik_ceiling_rate}
            disabled={isDisabled || disabledFields.includes("pik_ceiling_rate")}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
